// Briques hautes de l'aperçu du dossier : bandeau « prochaine audience »,
// quatre raccourcis chiffrés vers les onglets, derniers documents.
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { differenceInCalendarDays, format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../../lib/cn';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import { useDossierHearings } from '../../hooks/queries/useDossierHearings';
import { useDossierTabCounts } from '../../hooks/queries/useDossierTabCounts';
import { useDossierDiscussionUnreadCount } from '../../hooks/queries/useMessaging';
import { useTimeEntries } from '../../hooks/queries/useTimeEntries';
import { useBillingDocuments } from '../../hooks/queries/useBillingDocuments';
import { useAllDossierFiles } from '../../hooks/queries/useDossierFiles';
import { computeFinancialSummary } from '../../services/billing/billingService';
import type { Dossier } from '../../services/dossiers/dossierService';
import {
  FaCalendar,
  FaComments,
  FaFileInvoice,
  FaFolderOpen,
  FaListCheck,
  type AppIcon,
} from '../ui/FaIcons';

/** Montant HT des temps facturables pas encore facturés. */
export function unbilledAmount(
  entries: readonly {
    is_billable: boolean;
    invoiced_in_id: string | null;
    duration_minutes: number;
    hourly_rate_snapshot: number | null;
  }[],
): number {
  return entries
    .filter((e) => e.is_billable && !e.invoiced_in_id)
    .reduce((sum, e) => sum + (e.duration_minutes / 60) * (e.hourly_rate_snapshot ?? 0), 0);
}

export function euros(value: number): string {
  return `${Math.round(value).toLocaleString('fr-FR')} €`;
}

// ─── Prochaine audience ──────────────────────────────────────────────────────

export function NextHearingBand({ dossier }: { dossier: Dossier }) {
  const { data: hearings = [] } = useDossierHearings(dossier.id);
  const next = useMemo(() => {
    const now = Date.now();
    return [...hearings]
      .filter((h) => new Date(h.starts_at).getTime() >= now)
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())[0];
  }, [hearings]);

  if (!next) {
    return (
      <Link
        to={`/dossiers/${dossier.id}/agenda`}
        className="flex items-center gap-4 rounded-xl border border-neutral-200 border-l-[3px] border-l-neutral-300 bg-white py-2.5 pl-3.5 pr-4 transition-colors hover:border-neutral-300"
      >
        <span className="grid h-11 min-w-[54px] place-items-center rounded-lg bg-neutral-100 px-2 text-[17px] font-bold text-neutral-400">
          —
        </span>
        <div className="min-w-0">
          <small className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
            <FaCalendar className="h-[11px] w-[11px]" aria-hidden />
            Prochaine audience
          </small>
          <p className="m-0 mt-0.5 truncate text-sm text-cime-text-muted">Aucune audience à venir.</p>
        </div>
      </Link>
    );
  }

  const start = new Date(next.starts_at);
  const days = differenceInCalendarDays(start, new Date());
  const place = next.court ?? dossier.juridiction;

  return (
    <Link
      to={`/dossiers/${dossier.id}/agenda`}
      className="flex items-center gap-4 rounded-xl border border-neutral-200 border-l-[3px] border-l-[#ea7549] bg-white py-2.5 pl-3.5 pr-4 transition-colors hover:border-neutral-300 hover:border-l-[#ea7549]"
    >
      <span className="grid h-11 min-w-[54px] place-items-center rounded-lg bg-[#fdece6] px-2 text-[17px] font-bold tabular-nums text-[#c8431f]">
        {days === 0 ? 'Auj.' : `J-${days}`}
      </span>
      <div className="min-w-0">
        <small className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#c8431f]">
          <FaCalendar className="h-[11px] w-[11px]" aria-hidden />
          Prochaine audience
        </small>
        <p className="m-0 mt-0.5 truncate text-sm text-cime-text-primary">
          <b className="font-semibold">{next.label}</b> · {format(start, "EEE d MMM 'à' HH:mm", { locale: fr })}
          {place ? (
            <>
              <span className="mx-1.5 text-neutral-300">·</span>
              <span className="text-cime-text-muted">{place}</span>
            </>
          ) : null}
        </p>
      </div>
    </Link>
  );
}

// ─── Raccourcis chiffrés ─────────────────────────────────────────────────────

export function OverviewStats({ dossier }: { dossier: Dossier }) {
  const role = asCollaboratorRole(useAuthStore((s) => s.collaborator?.role));
  const canSeeBilling = canAccessFeature(role, 'billing_view');
  const { data: counts } = useDossierTabCounts(dossier.id);
  const unread = useDossierDiscussionUnreadCount(dossier.id);
  const { data: entries } = useTimeEntries({ dossier_id: dossier.id });
  const { data: billingDocs } = useBillingDocuments({ dossier_id: dossier.id });
  const toBill = useMemo(() => unbilledAmount(entries ?? []), [entries]);
  const financial = useMemo(() => computeFinancialSummary(billingDocs ?? []), [billingDocs]);
  const base = `/dossiers/${dossier.id}`;

  return (
    <div className={cn('grid grid-cols-2 gap-3.5', canSeeBilling ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
      <Stat
        to={`${base}/taches`}
        icon={FaListCheck}
        value={String(counts?.taches ?? 0)}
        label={(counts?.taches ?? 0) > 1 ? 'tâches ouvertes' : 'tâche ouverte'}
        flag={counts?.tachesRetard ? `${counts.tachesRetard} en retard` : undefined}
        late
      />
      <Stat
        to={`${base}/documents`}
        icon={FaFolderOpen}
        value={String(counts?.documents ?? 0)}
        label={(counts?.documents ?? 0) > 1 ? 'documents' : 'document'}
      />
      <Stat
        to={`${base}/discussions`}
        icon={FaComments}
        value={String(unread)}
        label={unread > 1 ? 'messages non lus' : 'message non lu'}
      />
      {canSeeBilling ? (
        <Stat
          to={`${base}/facturation`}
          icon={FaFileInvoice}
          value={euros(toBill)}
          label="à facturer"
          flag={financial.overdueTtc > 0 ? `${euros(financial.overdueTtc)} en retard` : undefined}
          late
        />
      ) : null}
    </div>
  );
}

function Stat({
  to,
  icon: Icon,
  value,
  label,
  flag,
  late,
}: {
  to: string;
  icon: AppIcon;
  value: string;
  label: string;
  flag?: string;
  late?: boolean;
}) {
  return (
    <Link
      to={to}
      className="grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-2 rounded-xl border border-neutral-200 bg-white px-4 py-3.5 transition-colors hover:border-cime-primary/40"
    >
      <span className="grid h-9 w-9 place-items-center rounded-[9px] bg-[#f1efe8] text-cime-primary">
        <Icon className="h-[15px] w-[15px]" aria-hidden />
      </span>
      <div className="leading-tight">
        <b className="block text-[21px] font-semibold tabular-nums text-cime-text-primary">{value}</b>
        <small className="text-xs text-cime-text-muted">{label}</small>
      </div>
      {flag ? (
        <span
          className={cn(
            'col-span-2 w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold',
            late ? 'bg-[#fdece6] text-[#c8431f]' : 'bg-neutral-100 text-cime-text-muted',
          )}
        >
          {flag}
        </span>
      ) : null}
    </Link>
  );
}

// ─── Derniers documents ──────────────────────────────────────────────────────

function fileKind(filename: string, mime: string): { label: string; className: string } {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (mime === 'application/pdf' || ext === 'pdf') return { label: 'PDF', className: 'bg-[#fdece6] text-[#c8431f]' };
  if (['doc', 'docx', 'odt'].includes(ext)) return { label: 'DOC', className: 'bg-[#e6eefb] text-[#2b5aa8]' };
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { label: 'XLS', className: 'bg-[#e8f0ec] text-cime-primary' };
  if (mime.startsWith('image/')) return { label: 'IMG', className: 'bg-[#f6efd8] text-[#8a6d1f]' };
  if (mime === 'message/rfc822' || ext === 'eml' || ext === 'msg') return { label: 'MAIL', className: 'bg-[#f1efe8] text-cime-text-muted' };
  return { label: ext.slice(0, 4).toUpperCase() || 'DOC', className: 'bg-neutral-100 text-cime-text-muted' };
}

export function RecentDocuments({ dossierId }: { dossierId: string }) {
  const { data: files } = useAllDossierFiles(dossierId);
  const recent = useMemo(
    () =>
      [...(files ?? [])]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 3),
    [files],
  );

  if (recent.length === 0) return <p className="text-xs text-neutral-400">Aucun document.</p>;

  return (
    <div className="-mx-4 -my-4">
      {recent.map((file) => {
        const kind = fileKind(file.filename, file.mime);
        return (
          <Link
            key={file.id}
            to={`/dossiers/${dossierId}/documents`}
            className="flex items-center gap-3 border-b border-neutral-50 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-neutral-50"
          >
            <span className={cn('grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg text-[10px] font-bold', kind.className)}>
              {kind.label}
            </span>
            <div className="min-w-0 leading-tight">
              <b className="block truncate text-[13px] font-medium text-cime-text-primary">{file.filename}</b>
              <small className="text-[11px] text-neutral-400">
                {formatDistanceToNow(new Date(file.created_at), { addSuffix: true, locale: fr })}
              </small>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
